import { useDsChatQueue } from 'hooks/useDsChatQueue';
import { shallowEqual, useSelector } from 'react-redux';
import { selectActiveDsChat } from 'redux/db/reducers';
import { getApprovedPlanIds } from 'redux/db/util';
import { RootReducer } from 'redux/reducers';
import * as S from './styles';

interface Props {
	planId: string;
	steps: Array<{ label: string }>;
}

const PlanAction = ({ planId, steps }: Props) => {
	const { dsChat, isApproved } = useSelector((r: RootReducer) => {
		const chat = selectActiveDsChat(r.db);
		const approvedIds = chat ? getApprovedPlanIds(chat.entries) : new Set<string>();
		return {
			dsChat: chat,
			isApproved: approvedIds.has(planId),
		};
	}, shallowEqual);

	const queue = useDsChatQueue(dsChat?.id ?? null);
	const clientKey = `approve:${planId}`;
	const isQueued = queue.items.some((e) => e.clientKey === clientKey);

	const onApproveClick = () => {
		if (!dsChat || isApproved || isQueued) return;
		queue.enqueue({
			label: 'Approve',
			clientKey,
			payload: { kind: 'approvePlan', planId },
		});
	};

	return (
		<S.Base>
			<S.Title>Plan</S.Title>
			<S.Steps>
				{steps.map((s, i) => (
					<S.Step key={i}>
						<S.StepIndex>{i + 1}</S.StepIndex>
						<S.StepLabel>{s.label}</S.StepLabel>
					</S.Step>
				))}
			</S.Steps>
			{isApproved ? (
				<S.Approved>
					<S.CheckIcon viewBox="0 0 16 16" aria-hidden>
						<path
							d="M3.5 8.5l3 3 6-7"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</S.CheckIcon>
					Approved
				</S.Approved>
			) : (
				<S.ApproveButton type="button" onClick={onApproveClick} $queued={isQueued}>
					{isQueued ? 'Approve · queued' : 'Approve'}
				</S.ApproveButton>
			)}
		</S.Base>
	);
};

export default PlanAction;
